import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ReactSVG } from 'react-svg';
import { Link } from 'react-router-dom';
import Notification from 'common-ui-components/dist/components/Atoms/Notification';
import Spinner from 'common-ui-components/dist/components/Molecules/Spinner';
import Anchor from 'common-ui-components/dist/components/Atoms/Anchor';
import { isEmpty, get } from 'lodash';
import QuickOrder from '../../../components/molecules/QuickOrder';
import { QUICK_ORDER_URL, CART_URL } from '../../../global/constants';
import {
  getLabelWithComponent,
  removeLastPartFromUrl,
  getContactUsLink,
} from '../../../utils/utils';
import './QuickOrderOverlay.scss';

const QuickOrderAddItemSuccessModal = React.lazy(() =>
  import('../../../components/templates/QuickOrderComponent/QuickOrderAddItemSuccessModal')
);

const QuickOrderOverlay = ({
  labels,
  config,
  shoppingCartDataError,
  addToCart,
  isCartItemAdded,
  entries,
  location,
}) => {
  const [inputValue, setInputValue] = useState();
  const [skuName, setSkuName] = useState('');
  const [showLoader, setShowLoader] = useState(false);
  let quickOrderElem = null;
  const pathname = get(location, 'pathname', '');
  const quickOrderUrl = `${removeLastPartFromUrl(pathname)}/${QUICK_ORDER_URL}`;
  const cartUrl = `${removeLastPartFromUrl(pathname)}/${CART_URL}`;
  const { multipleIconUrl, contactSupportHref, contactUsLink: eComContactUsLink } = config;
  const contactUsLink = getContactUsLink(contactSupportHref, eComContactUsLink);
  const contactUsAnchor = (
    <Anchor
      className="row cart-inline-error-contact-us-link"
      elementLocator="add-to-cart-invalid-sku-error-contact-us-link"
      to={contactUsLink}
    >
      {labels.contactWaters}
    </Anchor>
  );
  const inlineError =
    !isEmpty(shoppingCartDataError) && labels[shoppingCartDataError.code]
      ? getLabelWithComponent(labels[shoppingCartDataError.code], [inputValue, contactUsAnchor])
      : '';

  useEffect(() => {
    if (isCartItemAdded && quickOrderElem) {
      quickOrderElem.reset();
    }
  });

  useEffect(() => {
    if (!isEmpty(shoppingCartDataError)) {
      setShowLoader(false);
    }
  }, [shoppingCartDataError]);

  useEffect(() => {
    if (!isEmpty(entries) && isCartItemAdded) {
      const getSKUName = () => {
        const productObj = entries.filter(({ product }) => product.code === inputValue)[0];
        return get(productObj, 'product.name', '');
      };

      setSkuName(getSKUName());
      setShowLoader(false);
    }
  }, [entries]);

  const modalPopup = () => {
    setSkuName('');
    setInputValue('');
    setShowLoader(false);
    document.querySelector('body').classList.remove(`overlay-active__quickOrder`);
  };

  const renderQuickOrderSuccessModal = () => (
    <div
      className="row"
      role="button"
      tabIndex={0}
      onKeyPress={() => {
        /* default onKeyPress handler */
      }}
      onClick={() => modalPopup()}
    >
      <QuickOrderAddItemSuccessModal
        isModalAddItemOpen={Boolean(skuName)}
        setIsModalAddItemOpen={setSkuName}
        skuName={skuName}
        sku={`${labels.sku}: ${inputValue}`}
        heading={labels.itemAddedToCart}
        icon={config.checkmarkIcon}
        continueShoppingUrl={config.continueShoppingUrl}
        cartUrl={cartUrl}
        viewCartLabel={labels.viewCart}
        continueShoppingLabel={labels.buttonContinueShopping}
      />
    </div>
  );

  const renderQuickOrder = () => (
    <div className="quick-order-overlay">
      <div>
        <Spinner isLoading={showLoader} />
        <QuickOrder
          ref={elem => {
            quickOrderElem = elem;
          }}
          pageId="header-quick-order"
          elementLocator="header-quick-order"
          inputPlaceholder={labels.codePlaceholder}
          qtyPlaceholder={labels.qtyPlaceholder}
          submitButtonLabel={labels.addToCart}
          {...{
            onSubmit: (skuValue, qty) => {
              setInputValue(skuValue);
              addToCart(skuValue, qty);
              setShowLoader(true);
            },
          }}
        />
      </div>
      <div>
        <Link
          to={quickOrderUrl}
          className="row quick-order-multiple-item"
          data-locator="quick-order-multiple-item"
          aria-label={labels.addMultipleItems}
        >
          {<ReactSVG src={multipleIconUrl} />}
          {labels.addMultipleItems}
        </Link>
      </div>
      <div className="error-notification">
        {inlineError && !!inputValue && (
          <Notification
            variation="inline"
            type="error"
            description={inlineError}
            icon={config.removeAllItemsIconUrl}
            className="col-xs-12"
            elementLocator="header-quick-order-error-notification"
          />
        )}
      </div>
    </div>
  );
  return (
    <>
      {renderQuickOrder()}
      {renderQuickOrderSuccessModal()}
    </>
  );
};

QuickOrderOverlay.propTypes = {
  labels: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  config: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  shoppingCartDataError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  addToCart: PropTypes.func,
  isCartItemAdded: PropTypes.bool,
  entries: PropTypes.arrayOf(PropTypes.string),
  location: PropTypes.string,
};

QuickOrderOverlay.defaultProps = {
  config: {},
  labels: {},
  location: '',
  shoppingCartDataError: {},
  addToCart: () => {
    /* default addToCart handler */
  },
  isCartItemAdded: false,
  entries: [],
};

export default QuickOrderOverlay;
